import { useCallback, useMemo } from 'react';
import { ApolloError, NetworkStatus } from '@apollo/client';

import {
  MarketingActionsListQuery,
  MarketingSignalFieldsFragment,
  useMarketingActionsListQuery,
  useMarketingAdChoicesQuery,
  useMarketingAdsSearchTermsReportQuery,
  useMarketingAiAssistantsReportQuery,
  useMarketingBreakdownReportQuery,
  useMarketingCampaignChoicesQuery,
  useMarketingChannelOverlapReportQuery,
  useMarketingCohortsReportQuery,
  useMarketingContractsReportQuery,
  useMarketingCreativesReportQuery,
  useMarketingDataQualityReportQuery,
  useMarketingDigestHistoryListQuery,
  useMarketingDigestWeekTextQuery,
  useMarketingJourneyReportQuery,
  useMarketingLeadDynamicsReportQuery,
  useMarketingMatrixReportQuery,
  useMarketingOfflineConversionsReportQuery,
  useMarketingOverviewReportQuery,
  useMarketingPathCohortsReportQuery,
  useMarketingSaleByIdQuery,
  useMarketingSalesForecastReportQuery,
  useMarketingSalesReportQuery,
  useMarketingSearchDemandReportQuery,
  useMarketingSearchEngineReportQuery,
  useMarketingSearchTotalsProbeQuery,
  useMarketingSendgridEmailReportQuery,
  useMarketingSeriesReportQuery,
  useMarketingSignalsFeedQuery,
  useMarketingSitePageCardQuery,
  useMarketingSitePagesReportQuery,
  useMarketingSiteTrafficReportQuery,
  useMarketingSourcesStatusQuery,
  useMarketingTargetsListQuery,
  useMarketingTaxonomyReportQuery,
  useMarketingVisitorJourneyReportQuery,
  useMarketingVisitorSamplesListQuery,
  useMarketingVisitorTimelineCardQuery,
  useMarketingWeeklySnapshotsListQuery,
} from 'core/graphql';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import {
  IMarketingCompareRequestInput,
  marketingGqlCompare,
  marketingGqlDimension,
  marketingGqlFilter,
  marketingGqlGranularity,
  marketingGqlHaving,
  marketingQueryErrorCode,
} from './marketingApi';
import { marketingDigestActive } from './marketingIntelModel';
import { MARKETING_OFFLINE_ISSUES_LIMIT } from './marketingOfflineModel';
import {
  marketingChannelFromKey,
  marketingParseJson,
  marketingTodayWarsaw,
  MARKETING_SIGNAL_FEED_STATUSES,
} from './marketingModel';
import {
  IMarketingAction,
  IMarketingAdOption,
  IMarketingBreakdown,
  IMarketingContractsReport,
  IMarketingLeadDynamics,
  IMarketingOverlapCell,
  IMarketingPageCard,
  IMarketingPagesPage,
  IMarketingPathCohortRow,
  IMarketingVisitorJourney,
  IMarketingVisitorsPage,
  IMarketingVisitorTimeline,
  MarketingCampaignOptionList,
  IMarketingCohortRow,
  IMarketingCreativesPage,
  IMarketingDataQuality,
  IMarketingFilter,
  IMarketingAiReferralRow,
  IMarketingDemandRow,
  IMarketingDigest,
  IMarketingEmailReport,
  IMarketingForecastRow,
  IMarketingJourney,
  IMarketingMatrixCell,
  IMarketingOfflineConversions,
  IMarketingSaleRow,
  IMarketingOverview,
  IMarketingSalesPage,
  IMarketingSearchReport,
  IMarketingSearchTermsPage,
  IMarketingSearchTotals,
  IMarketingSeriesPoint,
  IMarketingSignal,
  IMarketingSnapshot,
  IMarketingSourceStatus,
  IMarketingTarget,
  IMarketingTaxonomy,
  IMarketingTrafficRow,
  MarketingDate,
  MarketingDimensionCode,
  MarketingGranularityCode,
  MarketingSearchDimensionCode,
  MarketingSearchEngineCode,
  MarketingSearchZoneCode,
  MarketingTrafficGroupCode,
} from './types';
import { useMarketingPermissions } from './useMarketingPermissions';

/**
 * ТЗ 0088 — чтение раздела «Маркетинг».
 *
 * Все хуки устроены одинаково (образец — `Esign/useContractAnalytics`):
 *  - `cache-and-network` + `notifyOnNetworkStatusChange`, и пока ответ на
 *    новые переменные не пришёл, отдаётся прошлый (`previousData`): смена
 *    фильтра иначе размонтировала бы графики на «Загрузка…» (грабли
 *    `query-vars-change-unmounts-subtree`);
 *  - `loading` — данных нет вовсе; `fetching` — перезапрос поверх
 *    показанных цифр (они тускнеют). Плановый опрос — НЕ `fetching`: экран
 *    не должен мигать раз в минуту;
 *  - без права (`canUse`) и на чужой вкладке (`enabled: false`) запрос не
 *    уходит вовсе, и `refetch` у такого запроса ничего не делает — перезапуск
 *    пропущенного запроса ушёл бы без переменных (грабли
 *    `refetch-by-name-skipped-query`);
 *  - `errorCode` — `extensions.code` отказа (`window_too_long`…): текст по
 *    коду переводит экран (`marketingErrorKey`).
 *
 * Ответы присваиваются типам `types.ts` — несовпадение формы ловит `tsc`.
 */

/** Раз в минуту, пока вкладка браузера видна («Пульс», источники). */
export const MARKETING_POLL_MS = 60 * 1000;

/**
 * Финальная проверка 0103 (F-E17): после первого ответа — `cache-first`.
 * Apollo 3.3 на любой `broadcastQueries()` (снятие соседнего запроса при
 * смене вкладки, мутация, запись в кеш) перезапускает `cache-and-network`
 * запрос В СЕТЬ: с `notifyOnNetworkStatusChange` он сообщает «грузится» —
 * и `forceUpdate` (микрозадача) прилетал в компонент, который React уже
 * снял: «Can't perform a React state update on an unmounted component»
 * при переключении вкладок и подвкладок. Смена переменных по-прежнему идёт
 * `cache-and-network` (`useQuery` заново ставит политику хука), опрос и
 * `refetch` — в сеть; без пересчёта на каждую запись в кеш.
 */
export const MARKETING_NEXT_FETCH_POLICY = 'cache-first' as const;

export interface IMarketingQueryState<T> {
  data: T | null;
  /** Данных ещё нет — первый запрос. */
  loading: boolean;
  /** Перезапрос поверх показанных данных (не плановый опрос). */
  fetching: boolean;
  error?: ApolloError;
  errorCode: string | null;
  /** Перечитать; у пропущенного запроса — ничего. */
  refetch: () => void;
}

export interface IMarketingQueryOptions {
  /** `false` — вкладка не открыта: запрос не уходит. */
  enabled?: boolean;
  /**
   * Панель справа читает ответ, который уже загрузила таблица вкладки
   * (`cache-first`): нового запроса нет, если данные уже в кеше; нет — один
   * запрос (одинаковый с таблицей в полёте Apollo склеивает).
   */
  cacheFirst?: boolean;
}

interface IRawResult {
  loading: boolean;
  networkStatus: NetworkStatus;
  error?: ApolloError;
  refetch: () => Promise<unknown>;
}

const useQueryState = <T>(
  raw: IRawResult,
  current: T | null,
  skip: boolean,
): IMarketingQueryState<T> => {
  const { refetch: rawRefetch } = raw;
  const refetch = useCallback(() => {
    if (skip) return;
    // Отказ перезапроса Apollo кладёт в `error` хука — здесь его глотаем,
    // чтобы не было необработанного промиса.
    rawRefetch().catch(() => undefined);
  }, [skip, rawRefetch]);
  return {
    data: current,
    loading: raw.loading && current === null,
    fetching: raw.loading && raw.networkStatus !== NetworkStatus.poll,
    error: raw.error,
    errorCode: marketingQueryErrorCode(raw.error),
    refetch,
  };
};

/* ── «Пульс» ─────────────────────────────────────────────────────────── */

export const useMarketingOverview = (
  filter: IMarketingFilter,
  granularity: MarketingGranularityCode,
  {
    enabled = true,
    compare = null,
    poll = true,
  }: IMarketingQueryOptions & {
    /** ТЗ 0103: с чем сравнивать; окно считает сервер (`compareInfo`). */
    compare?: IMarketingCompareRequestInput | null;
    /** Опрашивать в открытой вкладке (по умолчанию — да). */
    poll?: boolean;
  } = {},
): IMarketingQueryState<IMarketingOverview> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingOverviewReportQuery({
    variables: {
      filter: marketingGqlFilter(filter),
      granularity: marketingGqlGranularity(granularity),
      compare: marketingGqlCompare(compare),
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  usePageVisiblePoll(
    result.startPolling,
    result.stopPolling,
    MARKETING_POLL_MS,
    !skip && poll,
    result.refetch,
  );
  const current: IMarketingOverview | null =
    (result.data ?? result.previousData)?.marketingOverview ?? null;
  return useQueryState(result, current, skip);
};

/**
 * Финальная проверка 0103 (F-E15): начало истории CRM-лидов там, где
 * обзора окна нет («Неделя к неделе»). Поле от фильтров и окна не зависит —
 * спрашиваем обзор одного сегодняшнего дня без фильтров (дёшево, без
 * опроса, один ответ на день в кеше). `undefined` — ответа ещё нет,
 * `null` — лидов нет вовсе.
 */
export const useMarketingLeadHistoryFrom = ({
  enabled = true,
}: IMarketingQueryOptions = {}): MarketingDate | null | undefined => {
  const today = marketingTodayWarsaw();
  const filter = useMemo<IMarketingFilter>(
    () => ({ dateFrom: today, dateTo: today }),
    [today],
  );
  const overview = useMarketingOverview(filter, 'DAY', {
    enabled,
    poll: false,
  });
  if (!overview.data) return undefined;
  return overview.data.leadHistoryFrom ?? null;
};

/* ── Разрез («Каналы», «Курсы») ──────────────────────────────────────── */

export interface IMarketingBreakdownArgs {
  filter: IMarketingFilter;
  dimension: MarketingDimensionCode;
  parentKey?: string | null;
  withSeries?: boolean;
  granularity?: MarketingGranularityCode;
  limit?: number;
  offset?: number;
  /**
   * `-crmLeads` — по убыванию; `-delta_crmLeads` — по изменению (только со
   * сравнением); `null` — порядок сервера.
   */
  orderBy?: string | null;
  /** ТЗ 0103: сравнение — `previous` у строки и `compareInfo`. */
  compare?: IMarketingCompareRequestInput | null;
  /** Со сравнением — и строки, которые были только в окне сравнения. */
  includeVanished?: boolean;
  /** Быстрые фильтры (`MarketingRowPredicate`), до страниц. */
  having?: ReadonlyArray<string> | null;
  /** Строки с расходом не меньше (PLN) — «только активные». */
  minSpendPln?: string | null;
  minCrmLeads?: number | null;
}

/**
 * Ключ строки канала — кодом enum (`META`), как в фильтре и словарях:
 * сервер отдаёт в разрезе `CHANNEL` код базы (`meta`). Остальные разрезы —
 * как пришли.
 */
export const marketingNormalizeBreakdown = (
  data: IMarketingBreakdown,
): IMarketingBreakdown =>
  data.dimension === 'CHANNEL'
    ? {
        ...data,
        rows: data.rows.map((row) => ({
          ...row,
          key: marketingChannelFromKey(row.key),
        })),
      }
    : data;

export const useMarketingBreakdown = (
  args: IMarketingBreakdownArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingBreakdown> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingBreakdownReportQuery({
    variables: {
      filter: marketingGqlFilter(args.filter),
      dimension: marketingGqlDimension(args.dimension),
      parentKey: args.parentKey || null,
      withSeries: Boolean(args.withSeries),
      granularity: marketingGqlGranularity(args.granularity ?? 'WEEK'),
      limit: args.limit ?? null,
      offset: args.offset ?? 0,
      orderBy: args.orderBy || null,
      compare: marketingGqlCompare(args.compare),
      includeVanished: args.includeVanished ?? null,
      having: marketingGqlHaving(args.having),
      minSpendPln: args.minSpendPln ?? null,
      minCrmLeads: args.minCrmLeads ?? null,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const raw: IMarketingBreakdown | null =
    (result.data ?? result.previousData)?.marketingBreakdown ?? null;
  // Новый объект строк — только на новый ответ: иначе нормализация на
  // каждом рендере будила бы мемо таблицы и эффекты потребителей.
  const current = useMemo(
    () => (raw ? marketingNormalizeBreakdown(raw) : null),
    [raw],
  );
  return useQueryState(result, current, skip);
};

/* ── Матрица двух измерений («Каналы»: канал × курс) ─────────────────── */

export interface IMarketingMatrixArgs {
  filter: IMarketingFilter;
  /**
   * Строки — CHANNEL | FAMILY | OFFER | CAMPAIGN | AD (ТЗ 0103), столбцы —
   * CHANNEL | FAMILY | OFFER; разные.
   */
  rows?: MarketingDimensionCode;
  cols?: MarketingDimensionCode;
  /** ТЗ 0103: первые N строк по `orderBy` итога строки. */
  limit?: number | null;
  /** Показатель итога строки; по умолчанию сервера — `-sales`. */
  orderBy?: string | null;
}

/**
 * Ключ канала — кодом enum (`META`), как в разрезе и фильтре: сервер отдаёт
 * код базы (`meta`). Остальные измерения — как пришли.
 */
export const marketingNormalizeMatrix = (
  cells: ReadonlyArray<IMarketingMatrixCell>,
  rows: MarketingDimensionCode,
  cols: MarketingDimensionCode,
): IMarketingMatrixCell[] =>
  cells.map((cell) => ({
    ...cell,
    rowKey:
      rows === 'CHANNEL' ? marketingChannelFromKey(cell.rowKey) : cell.rowKey,
    colKey:
      cols === 'CHANNEL' ? marketingChannelFromKey(cell.colKey) : cell.colKey,
  }));

/**
 * Матрица «канал × курс» под таблицей «Каналов». Уходит, только пока
 * карточка видна (`enabled` — корень разреза по каналам без спуска).
 */
export const useMarketingMatrix = (
  {
    filter,
    rows = 'CHANNEL',
    cols = 'FAMILY',
    limit = null,
    orderBy = null,
  }: IMarketingMatrixArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingMatrixCell[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingMatrixReportQuery({
    variables: {
      filter: marketingGqlFilter(filter),
      rows: marketingGqlDimension(rows),
      cols: marketingGqlDimension(cols),
      limit,
      orderBy,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const raw: IMarketingMatrixCell[] | null =
    (result.data ?? result.previousData)?.marketingMatrix ?? null;
  // Новый массив — только на новый ответ (как у разреза).
  const current = useMemo(
    () => (raw ? marketingNormalizeMatrix(raw, rows, cols) : null),
    [raw, rows, cols],
  );
  return useQueryState(result, current, skip);
};

/* ── Ряд одной строки ────────────────────────────────────────────────── */

export const useMarketingSeries = (
  filter: IMarketingFilter | null,
  granularity: MarketingGranularityCode,
  {
    enabled = true,
    compare = null,
  }: IMarketingQueryOptions & {
    /** ТЗ 0103: корзина сравнения того же номера у каждой точки. */
    compare?: IMarketingCompareRequestInput | null;
  } = {},
): IMarketingQueryState<IMarketingSeriesPoint[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled || !filter;
  const result = useMarketingSeriesReportQuery({
    // Без фильтра запрос пропущен; переменные — заглушка для типа.
    variables: {
      filter: marketingGqlFilter(
        filter ?? { dateFrom: '1970-01-01', dateTo: '1970-01-01' },
      ),
      granularity: marketingGqlGranularity(granularity),
      compare: marketingGqlCompare(compare),
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  // Ряд другой строки — не «прошлые данные» этой: `previousData` здесь не
  // берём, иначе под заголовком новой строки полсекунды висел бы ряд старой.
  const current: IMarketingSeriesPoint[] | null = skip
    ? null
    : result.data?.marketingSeries ?? null;
  return useQueryState(result, current, skip);
};

/* ── Продажи окна («Качество данных») ────────────────────────────────── */

export interface IMarketingSalesArgs {
  filter: IMarketingFilter;
  onlySales: boolean;
  /** Точный уровень покрытия (`none`…`ad`); `null` — все. */
  coverage: string | null;
  limit: number;
  offset: number;
}

export const useMarketingSales = (
  args: IMarketingSalesArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSalesPage> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSalesReportQuery({
    variables: {
      filter: marketingGqlFilter(args.filter),
      onlySales: args.onlySales,
      coverage: args.coverage || null,
      limit: args.limit,
      offset: args.offset,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingSalesPage | null =
    (result.data ?? result.previousData)?.marketingSales ?? null;
  return useQueryState(result, current, skip);
};

/* ── Продажа по id (ТЗ 0091) ─────────────────────────────────────────── */

/** Id продажи в адресе (`insp=sale:<id>`) — целое; иначе `null`. */
export const marketingSaleIdOf = (key: string | null | undefined) => {
  if (!key || !/^\d{1,15}$/.test(key)) return null;
  return Number(key);
};

export interface IMarketingSaleByIdState
  extends IMarketingQueryState<IMarketingSaleRow> {
  /** Сервер ответил: такой продажи нет (`null`). */
  missing: boolean;
}

/**
 * Панель продажи, когда строки нет на загруженной странице таблицы: одна
 * продажа по id, без окна фильтра. Включает её панель и только тогда, когда
 * страница уже пришла и строки на ней нет (`enabled`) — иначе запрос не
 * уходит. Без `previousData`: строка прошлой продажи — не данные этой.
 * `missing` — сервер вернул `null` (id устарел после ночной пересборки,
 * продажу отменили); нечисловой id не спрашивается и тоже «нет».
 */
export const useMarketingSaleById = (
  key: string | null,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingSaleByIdState => {
  const { canUse } = useMarketingPermissions();
  const id = marketingSaleIdOf(key);
  const skip = !canUse || !enabled || id === null;
  const result = useMarketingSaleByIdQuery({
    variables: { id: id ?? 0 },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const answered = !skip && !result.loading && !result.error;
  const current: IMarketingSaleRow | null = skip
    ? null
    : result.data?.marketingSale ?? null;
  const state = useQueryState(result, current, skip);
  return {
    ...state,
    missing:
      enabled &&
      (id === null ||
        (answered && Boolean(result.data) && !result.data?.marketingSale)),
  };
};

/* ── «Качество данных» ───────────────────────────────────────────────── */

export const useMarketingDataQuality = (
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingDataQuality> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingDataQualityReportQuery({
    variables: { filter: marketingGqlFilter(filter) },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingDataQuality | null =
    (result.data ?? result.previousData)?.marketingDataQuality ?? null;
  return useQueryState(result, current, skip);
};

/* ── Источники ───────────────────────────────────────────────────────── */

export interface IMarketingSourcesOptions extends IMarketingQueryOptions {
  /** Шаг опроса; по умолчанию минута (чаще — пока идёт пересчёт). */
  pollMs?: number;
}

export const useMarketingSources = ({
  enabled = true,
  pollMs = MARKETING_POLL_MS,
}: IMarketingSourcesOptions = {}): IMarketingQueryState<
  IMarketingSourceStatus[]
> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSourcesStatusQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  usePageVisiblePoll(
    result.startPolling,
    result.stopPolling,
    pollMs,
    !skip,
    result.refetch,
  );
  const current: IMarketingSourceStatus[] | null =
    (result.data ?? result.previousData)?.marketingSources ?? null;
  return useQueryState(result, current, skip);
};

/* ── Офлайн-конверсии (ТЗ 0091) ──────────────────────────────────────── */

/**
 * «Настройки → Офлайн-конверсии»: журнал выгрузки продаж в Google Ads и
 * Meta, только чтение. Запрос уходит, только пока подвкладка открыта;
 * пока вкладка браузера видна — раз в минуту (журнал пишет утренняя задача,
 * но и прогон, запущенный командой, должен показаться без перезагрузки).
 * Итог прогона (`stats`, JSONString) разбирается здесь — на новый ответ, а
 * не на каждый рендер.
 */
export const useMarketingOfflineConversions = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingOfflineConversions> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingOfflineConversionsReportQuery({
    variables: { limit: MARKETING_OFFLINE_ISSUES_LIMIT },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  usePageVisiblePoll(
    result.startPolling,
    result.stopPolling,
    MARKETING_POLL_MS,
    !skip,
    result.refetch,
  );
  const raw =
    (result.data ?? result.previousData)?.marketingOfflineConversions ?? null;
  const current = useMemo<IMarketingOfflineConversions | null>(
    () =>
      raw
        ? {
            enabled: raw.enabled,
            googleEnabled: raw.googleEnabled,
            metaEnabled: raw.metaEnabled,
            retractEnabled: raw.retractEnabled,
            since: raw.since ?? null,
            lastRun: raw.lastRun
              ? {
                  status: raw.lastRun.status,
                  startedAt: raw.lastRun.startedAt ?? null,
                  finishedAt: raw.lastRun.finishedAt ?? null,
                  errorMessage: raw.lastRun.errorMessage ?? null,
                  stats: marketingParseJson(raw.lastRun.stats),
                }
              : null,
            counts7d: raw.counts7d,
            counts30d: raw.counts30d,
            issues: raw.issues,
          }
        : null,
    [raw],
  );
  return useQueryState(result, current, skip);
};

/* ── Справочники ─────────────────────────────────────────────────────── */

/**
 * Справочники нужны не только «Настройкам»: из них же варианты фильтра
 * (семейства, юрлица) — поэтому страница держит этот запрос всегда.
 */
export const useMarketingTaxonomy = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingTaxonomy> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingTaxonomyReportQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingTaxonomy | null =
    (result.data ?? result.previousData)?.marketingTaxonomy ?? null;
  return useQueryState(result, current, skip);
};

/* ══ Этап 2 ═════════════════════════════════════════════════════════════ */

/* ── «Креативы» ──────────────────────────────────────────────────────── */

export interface IMarketingCreativesArgs {
  filter: IMarketingFilter;
  fatigueOnly: boolean;
  /** `video`…; `null` — любой формат. */
  format: string | null;
  /** Ряд по дням — у первых 50 строк страницы; просим, только когда нужен. */
  withSeries?: boolean;
  /** `-spendPln` — по убыванию; `null` — порядок сервера (расход↓). */
  orderBy: string | null;
  limit: number;
  offset: number;
  /**
   * Ровно эти объявления — мимо измерений фильтра, формата и «только
   * выгорающих» (окно, модель и юрлицо действуют). `[]` — пусто; не задано —
   * без отбора.
   */
  adIds?: string[] | null;
  /** ТЗ 0103: сравнение — `previous` у строки и `compareInfo`. */
  compare?: IMarketingCompareRequestInput | null;
  /** Быстрые фильтры (`MarketingRowPredicate`), до страниц. */
  having?: ReadonlyArray<string> | null;
  /** Строки с расходом не меньше (PLN) — «только активные». */
  minSpendPln?: string | null;
  minCrmLeads?: number | null;
  /** format | family | offer | campaign | adset | status → `groups`. */
  groupBy?: string | null;
  /** Подстрока имени, id, заголовка или кампании. */
  search?: string | null;
}

export const useMarketingCreatives = (
  args: IMarketingCreativesArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingCreativesPage> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingCreativesReportQuery({
    variables: {
      filter: marketingGqlFilter(args.filter),
      fatigueOnly: args.fatigueOnly,
      format: args.format || null,
      withSeries: Boolean(args.withSeries),
      orderBy: args.orderBy || null,
      limit: args.limit,
      offset: args.offset,
      adIds: args.adIds ?? null,
      compare: marketingGqlCompare(args.compare),
      having: marketingGqlHaving(args.having),
      minSpendPln: args.minSpendPln ?? null,
      minCrmLeads: args.minCrmLeads ?? null,
      groupBy: args.groupBy || null,
      search: args.search?.trim() || null,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingCreativesPage | null =
    (result.data ?? result.previousData)?.marketingCreatives ?? null;
  return useQueryState(result, current, skip);
};

/* ── «Путь клиента» и когорты ────────────────────────────────────────── */

export const useMarketingJourney = (
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingJourney> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingJourneyReportQuery({
    variables: { filter: marketingGqlFilter(filter) },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingJourney | null =
    (result.data ?? result.previousData)?.marketingJourney ?? null;
  return useQueryState(result, current, skip);
};

export interface IMarketingCohortsArgs {
  /** WEEK | MONTH; DAY сервер не принимает (`bad_granularity`). */
  granularity?: MarketingGranularityCode;
  /** channel | family | path_kind; `null` — без разбивки. */
  splitBy?: string | null;
  /** single | multi; `null` — все пути. */
  pathKind?: string | null;
}

export const useMarketingCohorts = (
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
  {
    granularity = 'WEEK',
    splitBy = null,
    pathKind = null,
  }: IMarketingCohortsArgs = {},
): IMarketingQueryState<IMarketingCohortRow[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingCohortsReportQuery({
    variables: {
      filter: marketingGqlFilter(filter),
      granularity: marketingGqlGranularity(granularity),
      splitBy: splitBy || null,
      pathKind: pathKind || null,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingCohortRow[] | null =
    (result.data ?? result.previousData)?.marketingCohorts ?? null;
  return useQueryState(result, current, skip);
};

/* ── ТЗ 0103, этап 3: посетители и страницы сайта ────────────────────── */

/**
 * «Путь клиента › Посетители»: корзины до заявки, матрица каналов, типичные
 * пути. Сравнение — окно сервера (`compareInfo`); шаг нужен только правилу
 * «год назад».
 */
export const useMarketingVisitorJourney = (
  filter: IMarketingFilter,
  granularity: MarketingGranularityCode,
  {
    enabled = true,
    compare = null,
  }: IMarketingQueryOptions & {
    compare?: IMarketingCompareRequestInput | null;
  } = {},
): IMarketingQueryState<IMarketingVisitorJourney> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingVisitorJourneyReportQuery({
    variables: {
      filter: marketingGqlFilter(filter),
      granularity: marketingGqlGranularity(granularity),
      compare: marketingGqlCompare(compare),
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingVisitorJourney | null =
    (result.data ?? result.previousData)?.marketingVisitorJourney ?? null;
  return useQueryState(result, current, skip);
};

export interface IMarketingVisitorsArgs {
  filter: IMarketingFilter;
  limit: number;
  offset: number;
}

/** «Примеры путей»: анонимные посетители заявок окна, свежие первыми. */
export const useMarketingVisitors = (
  { filter, limit, offset }: IMarketingVisitorsArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingVisitorsPage> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingVisitorSamplesListQuery({
    variables: {
      filter: marketingGqlFilter(filter),
      limit,
      offset,
      onlyLinked: true,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingVisitorsPage | null =
    (result.data ?? result.previousData)?.marketingVisitors ?? null;
  return useQueryState(result, current, skip);
};

/**
 * Таймлайн посетителя (панель справа). От окна не зависит; неизвестный id —
 * `data === null` при пришедшем ответе (`notFound`).
 */
export const useMarketingVisitor = (
  id: string | null,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingVisitorTimeline> & { notFound: boolean } => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled || !id;
  const result = useMarketingVisitorTimelineCardQuery({
    variables: { id: id ?? '' },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingVisitorTimeline | null =
    result.data?.marketingVisitor ?? null;
  const state = useQueryState(result, current, skip);
  return {
    ...state,
    notFound: !skip && Boolean(result.data) && !result.data?.marketingVisitor,
  };
};

export interface IMarketingPagesArgs {
  filter: IMarketingFilter;
  compare?: IMarketingCompareRequestInput | null;
  granularity: MarketingGranularityCode;
  /** Типы страниц (ИЛИ); пусто — все. */
  pageTypes?: ReadonlyArray<string> | null;
  familySlug?: string | null;
  search?: string | null;
  /** Показатель или `delta_<показатель>` (только со сравнением). */
  orderBy?: string | null;
  limit: number;
  offset?: number;
}

/** «Путь клиента › Страницы»: конверсия страниц сайта с окном сравнения. */
export const useMarketingPages = (
  args: IMarketingPagesArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingPagesPage> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSitePagesReportQuery({
    variables: {
      filter: marketingGqlFilter(args.filter),
      compare: marketingGqlCompare(args.compare),
      granularity: marketingGqlGranularity(args.granularity),
      pageTypes: args.pageTypes?.length ? [...args.pageTypes] : null,
      familySlug: args.familySlug || null,
      search: args.search?.trim() || null,
      orderBy: args.orderBy || null,
      limit: args.limit,
      offset: args.offset ?? 0,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingPagesPage | null =
    (result.data ?? result.previousData)?.marketingPages ?? null;
  return useQueryState(result, current, skip);
};

/**
 * Карточка страницы (панель справа): ряд шага со сравнением, источники
 * входа, следующие страницы, запросы GSC. Тело панели монтируется на каждую
 * страницу заново — чужих «прошлых данных» нет.
 */
export const useMarketingPage = (
  path: string | null,
  filter: IMarketingFilter,
  granularity: MarketingGranularityCode,
  {
    enabled = true,
    compare = null,
  }: IMarketingQueryOptions & {
    compare?: IMarketingCompareRequestInput | null;
  } = {},
): IMarketingQueryState<IMarketingPageCard> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled || !path;
  const result = useMarketingSitePageCardQuery({
    variables: {
      path: path ?? '',
      filter: marketingGqlFilter(filter),
      granularity: marketingGqlGranularity(granularity),
      compare: marketingGqlCompare(compare),
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingPageCard | null =
    (result.data ?? result.previousData)?.marketingPage ?? null;
  return useQueryState(result, current, skip);
};

/* ── ТЗ 0103: динамика лидов, многоканальные когорты, договоры ───────── */

export interface IMarketingLeadDynamicsArgs {
  filter: IMarketingFilter;
  granularity: MarketingGranularityCode;
  compare?: IMarketingCompareRequestInput | null;
  /** none | channel | family | offer | campaign | ad | … */
  groupBy: string;
  /** 1…20, остальное — `other`. */
  top?: number;
}

/**
 * Разбивка главного графика «Лидов и продаж» (`split`): CRM-лиды корзин по
 * каналам / курсам / предложениям, продажи их лидов-конверсий; с
 * `compare` — итог корзины сравнения того же номера.
 */
export const useMarketingLeadDynamics = (
  args: IMarketingLeadDynamicsArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingLeadDynamics> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingLeadDynamicsReportQuery({
    variables: {
      filter: marketingGqlFilter(args.filter),
      granularity: marketingGqlGranularity(args.granularity),
      compare: marketingGqlCompare(args.compare),
      groupBy: args.groupBy,
      top: args.top ?? null,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingLeadDynamics | null =
    (result.data ?? result.previousData)?.marketingLeadDynamics ?? null;
  return useQueryState(result, current, skip);
};

/** Многоканальные когорты: одиночный путь против многоканального. */
export const useMarketingPathCohorts = (
  filter: IMarketingFilter,
  groupBy: string,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingPathCohortRow[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingPathCohortsReportQuery({
    variables: { filter: marketingGqlFilter(filter), groupBy },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingPathCohortRow[] | null =
    (result.data ?? result.previousData)?.marketingPathCohorts ?? null;
  return useQueryState(result, current, skip);
};

/** «Каналы вместе»: ячейки «канал × канал» (код базы `meta`). */
export const useMarketingChannelOverlap = (
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingOverlapCell[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingChannelOverlapReportQuery({
    variables: { filter: marketingGqlFilter(filter) },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingOverlapCell[] | null =
    (result.data ?? result.previousData)?.marketingChannelOverlap ?? null;
  return useQueryState(result, current, skip);
};

/**
 * Отправленные договоры окна — только числа. Окно другой сущности (панель
 * справа) — не «прошлые данные» этой: `freshOnly` не берёт `previousData`.
 */
export const useMarketingContracts = (
  filter: IMarketingFilter,
  granularity: MarketingGranularityCode,
  {
    enabled = true,
    compare = null,
    freshOnly = false,
  }: IMarketingQueryOptions & {
    compare?: IMarketingCompareRequestInput | null;
    freshOnly?: boolean;
  } = {},
): IMarketingQueryState<IMarketingContractsReport> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingContractsReportQuery({
    variables: {
      filter: marketingGqlFilter(filter),
      granularity: marketingGqlGranularity(granularity),
      compare: marketingGqlCompare(compare),
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  let current: IMarketingContractsReport | null = null;
  if (!skip) {
    current = freshOnly
      ? result.data?.marketingContracts ?? null
      : (result.data ?? result.previousData)?.marketingContracts ?? null;
  }
  return useQueryState(result, current, skip);
};

type RawAction = MarketingActionsListQuery['marketingActions'][number];

/**
 * Действие экрана: JSONString параметров и доказательств разобран, id — как
 * строки (`signalId` — `ID`, `entityId` — `ID`).
 */
export const marketingActionFromRaw = (raw: RawAction): IMarketingAction => ({
  id: raw.id,
  source: raw.source,
  kind: raw.kind,
  severity: raw.severity,
  titleKey: raw.titleKey,
  params: marketingParseJson(raw.params),
  metrics: marketingParseJson(raw.metrics),
  valuePlnEst: raw.valuePlnEst ?? null,
  valueKind: raw.valueKind ?? null,
  valueWindowDays: raw.valueWindowDays ?? null,
  familyValuePlnEst: raw.familyValuePlnEst ?? null,
  familyKeywords: raw.familyKeywords ?? null,
  familySlug: raw.familySlug ?? null,
  entityId: raw.entityId ?? null,
  entityName: raw.entityName ?? null,
  channel: raw.channel ?? null,
  inspector: raw.inspector ?? null,
  link: raw.link,
  signalId: raw.signalId ?? null,
  windowFrom: raw.windowFrom ?? null,
  windowTo: raw.windowTo ?? null,
  lastSeen: raw.lastSeen ?? null,
});

/** Действий «Сегодня» за запрос (сервер ранжирует, ≤ 200). */
export const MARKETING_ACTIONS_LIMIT = 50;

/**
 * «Сегодня»: ранжированные действия сервера (открытые сигналы и гипотезы
 * бюджета). Опрос — раз в минуту, пока вкладка браузера видна.
 */
export const useMarketingActions = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingAction[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingActionsListQuery({
    variables: { limit: MARKETING_ACTIONS_LIMIT },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  usePageVisiblePoll(
    result.startPolling,
    result.stopPolling,
    MARKETING_POLL_MS,
    !skip,
    result.refetch,
  );
  const raw = (result.data ?? result.previousData)?.marketingActions ?? null;
  // Разбор JSON — на новый ответ, а не на каждый рендер.
  const current = useMemo(
    () => (raw ? raw.map(marketingActionFromRaw) : null),
    [raw],
  );
  return useQueryState(result, current, skip);
};

/** Вариантов выбора «Объявления» за поиск. */
export const MARKETING_AD_CHOICES_LIMIT = 30;

/**
 * Выбор «Объявления»: поиск сервера среди объявлений окна (имя, id,
 * заголовок, кампания), первые по расходу. Уходит, только пока список
 * открыт (`enabled`); текст поиска — уже успокоившийся.
 */
export const useMarketingAdOptions = (
  window: { dateFrom: MarketingDate; dateTo: MarketingDate },
  search: string,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingAdOption[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingAdChoicesQuery({
    variables: {
      filter: marketingGqlFilter({
        dateFrom: window.dateFrom,
        dateTo: window.dateTo,
      }),
      search: search.trim() || null,
      limit: MARKETING_AD_CHOICES_LIMIT,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingAdOption[] | null =
    (result.data ?? result.previousData)?.marketingCreatives.rows ?? null;
  return useQueryState(result, current, skip);
};

/* ── План ────────────────────────────────────────────────────────────── */

/** Строки плана месяца (`month` — первое число, `YYYY-MM-01`). */
export const useMarketingTargets = (
  month: string,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingTarget[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingTargetsListQuery({
    variables: { month },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  // План другого месяца — не «прошлые данные» этого: без `previousData`,
  // иначе под сентябрём полсекунды висели бы строки августа.
  const current: IMarketingTarget[] | null = skip
    ? null
    : result.data?.marketingTargets ?? null;
  return useQueryState(result, current, skip);
};

/* ── Сигналы ─────────────────────────────────────────────────────────── */

/**
 * Строка сигнала экрана: JSONString доказательств и параметров разобран
 * (сервер отдаёт их строкой JSON), мусор — пустой объект.
 */
export const marketingSignalFromRaw = (
  raw: MarketingSignalFieldsFragment,
): IMarketingSignal => ({
  id: raw.id,
  kind: raw.kind,
  severity: raw.severity,
  status: raw.status,
  entityId: raw.entityId ?? null,
  entityName: raw.entityName ?? null,
  familySlug: raw.familySlug ?? null,
  channel: raw.channel ?? null,
  windowFrom: raw.windowFrom,
  windowTo: raw.windowTo,
  metrics: marketingParseJson(raw.metrics),
  params: marketingParseJson(raw.params),
  firstSeen: raw.firstSeen,
  lastSeen: raw.lastSeen,
  ackBy: raw.ackBy ?? null,
});

export interface IMarketingSignalsArgs {
  /** По умолчанию — открытые и принятые (лента «Пульса»). */
  status?: string[];
  kinds?: string[] | null;
  limit?: number;
}

export const useMarketingSignals = (
  {
    status = MARKETING_SIGNAL_FEED_STATUSES,
    kinds = null,
    limit = 100,
  }: IMarketingSignalsArgs = {},
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSignal[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSignalsFeedQuery({
    variables: { status, kinds: kinds?.length ? kinds : null, limit },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const raw = (result.data ?? result.previousData)?.marketingSignals ?? null;
  // Разбор JSON — на новый ответ, а не на каждый рендер.
  const current = useMemo(
    () => (raw ? raw.map(marketingSignalFromRaw) : null),
    [raw],
  );
  return useQueryState(result, current, skip);
};

/* ── Кампании площадки для «Сопоставить с кампанией» ─────────────────── */

/**
 * Кампании площадки, имя которых содержит `search` (сервер: первые 50 по
 * расходу за 30 дней). Поиск печатается в поле — сюда приходит уже
 * успокоившийся текст (`useDebouncedValue` у выбора).
 */
export const useMarketingCampaignOptions = (
  platform: string | null,
  search: string,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<MarketingCampaignOptionList> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled || !platform;
  const result = useMarketingCampaignChoicesQuery({
    variables: { platform: platform ?? '', search: search.trim() || null },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: MarketingCampaignOptionList | null =
    (result.data ?? result.previousData)?.marketingCampaignOptions ?? null;
  return useQueryState(result, current, skip);
};

/* ══ Этап 3: поиск, сайт, спрос ═════════════════════════════════════════ */

/* ── Отчёт поисковика ────────────────────────────────────────────────── */

export interface IMarketingSearchArgs {
  filter: IMarketingFilter;
  engine: MarketingSearchEngineCode;
  dimension: MarketingSearchDimensionCode;
  /** `true` — брендовые, `false` — небрендовые, `null` — все. */
  brand: boolean | null;
  zone: MarketingSearchZoneCode | null;
  /** Семейство строк; `none` — без семейства; `null` — все. */
  familySlug: string | null;
  /** Подстрока запроса или страницы (уже успокоившаяся). */
  search: string;
  /** `-clicks` — по убыванию; `null` — порядок сервера (клики↓). */
  orderBy: string | null;
  limit: number;
  offset: number;
}

/**
 * Отчёт ОДНОГО поисковика (Google или Bing — никогда вместе). Фильтр
 * раздела действует окном; каналы, курсы и офферы общего фильтра к поиску
 * не относятся — сервер их не читает.
 */
export const useMarketingSearch = (
  args: IMarketingSearchArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSearchReport> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSearchEngineReportQuery({
    variables: {
      filter: marketingGqlFilter(args.filter),
      engine: args.engine,
      dimension: args.dimension,
      brand: args.brand,
      zone: args.zone,
      familySlug: args.familySlug || null,
      search: args.search.trim() || null,
      orderBy: args.orderBy || null,
      limit: args.limit,
      offset: args.offset,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingSearchReport | null =
    (result.data ?? result.previousData)?.marketingSearch ?? null;
  return useQueryState(result, current, skip);
};

/**
 * Итог поисковика за ПРОШЛОЕ окно (для изменения плиток) и его дни — чтобы
 * сравнивать только с окном, собранным целиком. Другое окно — не «прошлые
 * данные» этого: `previousData` не берём.
 */
export const useMarketingSearchTotals = (
  filter: IMarketingFilter,
  engine: MarketingSearchEngineCode,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSearchTotals> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSearchTotalsProbeQuery({
    variables: { filter: marketingGqlFilter(filter), engine },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingSearchTotals | null = skip
    ? null
    : result.data?.marketingSearch ?? null;
  return useQueryState(result, current, skip);
};

/* ── Сайт: трафик GA4, AI-ассистенты ───────────────────────────────── */

export const useMarketingTraffic = (
  filter: IMarketingFilter,
  groupBy: MarketingTrafficGroupCode,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingTrafficRow[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSiteTrafficReportQuery({
    variables: { filter: marketingGqlFilter(filter), groupBy },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingTrafficRow[] | null =
    (result.data ?? result.previousData)?.marketingTraffic ?? null;
  return useQueryState(result, current, skip);
};

export const useMarketingAiReferrals = (
  filter: IMarketingFilter,
  { enabled = true, cacheFirst = false }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingAiReferralRow[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingAiAssistantsReportQuery({
    variables: { filter: marketingGqlFilter(filter) },
    skip,
    fetchPolicy: cacheFirst ? 'cache-first' : 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingAiReferralRow[] | null =
    (result.data ?? result.previousData)?.marketingAiReferrals ?? null;
  return useQueryState(result, current, skip);
};

/* ── Спрос и поисковые запросы Google Ads ────────────────────────────── */

export const useMarketingDemand = (
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingDemandRow[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSearchDemandReportQuery({
    variables: { filter: marketingGqlFilter(filter) },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingDemandRow[] | null =
    (result.data ?? result.previousData)?.marketingDemand ?? null;
  return useQueryState(result, current, skip);
};

export interface IMarketingSearchTermsArgs {
  filter: IMarketingFilter;
  familySlug: string | null;
  /** `-spendPln` — по убыванию; `null` — порядок сервера (расход↓). */
  orderBy: string | null;
  limit: number;
  offset: number;
}

export const useMarketingSearchTerms = (
  args: IMarketingSearchTermsArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSearchTermsPage> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingAdsSearchTermsReportQuery({
    variables: {
      filter: marketingGqlFilter(args.filter),
      familySlug: args.familySlug || null,
      orderBy: args.orderBy || null,
      limit: args.limit,
      offset: args.offset,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingSearchTermsPage | null =
    (result.data ?? result.previousData)?.marketingSearchTerms ?? null;
  return useQueryState(result, current, skip);
};

/* ══ Этап 4: прогноз, недельный снимок, AI-сводка, письма ═══════════════ */

/**
 * Прогноз продаж месяца по семействам (`month` — первое число; `null` —
 * текущий месяц сервера). Прогноз другого месяца — не «прошлые данные»
 * этого: `previousData` не берём.
 */
export const useMarketingForecast = (
  month: MarketingDate | null,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingForecastRow[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSalesForecastReportQuery({
    variables: { month, familySlug: null },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingForecastRow[] | null = skip
    ? null
    : result.data?.marketingForecast ?? null;
  return useQueryState(result, current, skip);
};

/** Шаг опроса статуса сводки, пока она готовится. */
export const MARKETING_DIGEST_POLL_MS = 10 * 1000;

/** Сколько недель истории показывает «AI-разбор». */
export const MARKETING_DIGEST_HISTORY_LIMIT = 12;

/** Недельные снимки (только `summary`), новые сверху. */
export const useMarketingSnapshots = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingSnapshot[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingWeeklySnapshotsListQuery({
    variables: { limit: MARKETING_DIGEST_HISTORY_LIMIT, isoWeek: null },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingSnapshot[] | null =
    (result.data ?? result.previousData)?.marketingSnapshots ?? null;
  return useQueryState(result, current, skip);
};

/**
 * История AI-сводок без текста. Пока хоть одна `queued` / `running` —
 * опрос раз в 10 с (и только в видимой вкладке): статус сменится без
 * действия человека.
 */
export const useMarketingDigests = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingDigest[]> & {
  /** Хоть одна сводка ещё готовится. */
  active: boolean;
} => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingDigestHistoryListQuery({
    variables: { limit: MARKETING_DIGEST_HISTORY_LIMIT * 3 },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingDigest[] | null =
    (result.data ?? result.previousData)?.marketingDigests ?? null;
  const active = Boolean(
    current?.some((digest) => marketingDigestActive(digest.status)),
  );
  usePageVisiblePoll(
    result.startPolling,
    result.stopPolling,
    MARKETING_DIGEST_POLL_MS,
    !skip && active,
    result.refetch,
  );
  return { ...useQueryState(result, current, skip), active };
};

/**
 * Сводки одной недели с текстом. Неделя другая — не «прошлые данные» этой:
 * без `previousData`, иначе под новой неделей полсекунды висел бы текст
 * старой.
 */
export const useMarketingDigestWeek = (
  isoWeek: string | null,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingDigest[]> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled || !isoWeek;
  const result = useMarketingDigestWeekTextQuery({
    variables: { isoWeek: isoWeek ?? '', limit: 20 },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingDigest[] | null = skip
    ? null
    : result.data?.marketingDigests ?? null;
  return useQueryState(result, current, skip);
};

/** Письма SendGrid за окно фильтра («Путь клиента»). */
export const useMarketingEmail = (
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingEmailReport> => {
  const { canUse } = useMarketingPermissions();
  const skip = !canUse || !enabled;
  const result = useMarketingSendgridEmailReportQuery({
    variables: { filter: marketingGqlFilter(filter) },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingEmailReport | null =
    (result.data ?? result.previousData)?.marketingEmail ?? null;
  return useQueryState(result, current, skip);
};
